import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Grid,
  Typography,
  useTheme,
} from '@mui/material';
import { IPrescriptionsMedication, IMedication, Statuses } from 'API';
import { PrescriptionMedicationDialogMobile } from './PrescriptionMedicationDialogMobile';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { GreenVi } from 'Images';
import { ListDivider } from 'Components';
import { isPrescriptionMedication } from 'Utils';
import { Fragment, useState } from 'react';

interface PrescriptionMedicationsListProps {
  prescriptionMedications: IPrescriptionsMedication[] | IMedication[];
  prescriptionStatuses: Statuses[];
  currentStatus: number;
}

const gridItemSpacingStyleDesktop = { width: '29%', maxWidth: '323px' };

const medicationFieldStyleDesktop = {
  ...gridItemSpacingStyleDesktop,
  display: 'flex',
  alignItems: 'center',
  fontSize: '16px',
  fontWeight: '400',
  lineHeight: '21.79px',
  paddingRight: '5px',
};
export const PrescriptionMedicationsList = (
  props: PrescriptionMedicationsListProps
) => {
  const theme = useTheme();
  const isDesktop = useIsDesktop();
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const dictionary = useDictionary(
    Dictionary,
    DICTIONARY.MedicationsAndPrescriptionsLobby
  );

  // isSupplied return true if the current status is supplied or partially supplied
  const isSupplied =
    props.prescriptionStatuses &&
    props.prescriptionStatuses.find(
      (status) =>
        (status.label === dictionary.supply ||
          status.label === dictionary.partialSupplied) &&
        status.calcStatus === props.currentStatus
    );

  const medicationsGridTitles = [
    dictionary.nameOfTheMedication,
    dictionary.dosageText,
    dictionary.notes,
    isSupplied ? dictionary.status : undefined,
  ];
  const { prescriptionMedications: medications } = props;

  const medicationsAmountTitleStyle = {
    marginBottom: '16px',
    fontSize: '18px',
    lineHeight: '25px',
    fontWeight: '700',
    [theme.breakpoints.up('sm')]: {
      marginBottom: '56px',
      fontSize: '20px',
      lineHeight: '24px',
    },
  };

  const medicationsGridTitlesDesktop = (
    <Grid container item sx={{ marginBottom: '15px', paddingLeft: '8px' }}>
      {medicationsGridTitles.map((title, index, arr) => (
        <Grid
          item
          key={index}
          sx={
            isSupplied && arr.length - 1 === index
              ? { maxWidth: '100px' }
              : gridItemSpacingStyleDesktop
          }
        >
          <Typography fontWeight={700} fontSize={'18px'} lineHeight={'24.51px'}>
            {title}
          </Typography>
        </Grid>
      ))}
    </Grid>
  );

  const medicationsDetailsListDesktop = (
    <Grid container item>
      {medications.map((m, index) => (
        <Fragment key={index}>
          <Grid
            item
            container
            sx={{ minHeight: '69px', paddingLeft: '8px' }}
            alignItems='center'
          >
            <Grid item sx={medicationFieldStyleDesktop}>
              {m.medicatioN_NAME}
            </Grid>
            <Grid item sx={medicationFieldStyleDesktop}>
              {dictionary.dosageDetails(
                m.dosage,
                m.doseperday,
                m.fornumberdays
              )}
            </Grid>
            {isPrescriptionMedication(m) && (
              <>
                <Grid item sx={medicationFieldStyleDesktop}>
                  {m.medremark}
                </Grid>
                <Grid item>
                  {isSupplied && m.supplyind === 1 && (
                    <Grid gap={1} item container alignItems='center'>
                      <Avatar
                        src={GreenVi}
                        alt={''}
                        sx={{
                          width: '16px',
                          height: '16px',
                        }}
                      />
                      <Typography component='span'>
                        {dictionary.supplied}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </>
            )}
          </Grid>
          <ListDivider />
        </Fragment>
      ))}
    </Grid>
  );

  return isDesktop ? (
    <>
      <Typography sx={medicationsAmountTitleStyle} component='h3'>
        {dictionary.prescriptionMedicationsAmount(medications.length)}
      </Typography>
      <Grid container direction={'column'}>
        {medicationsGridTitlesDesktop}
        <ListDivider />
        {medicationsDetailsListDesktop}
      </Grid>
    </>
  ) : (
    <Accordion
      sx={{
        width: '100%',
        boxShadow: '0px 3px 21px rgba(45, 112, 217, 0.15)',
        borderRadius: '16px !important',
        '&:before': {
          display: 'none',
        },
      }}
    >
      <AccordionSummary
        onClick={() => {
          setIsExpanded(!isExpanded);
        }}
        sx={{
          p: '20px 24px',
          boxShadow: isExpanded ? '0px 4px 28px rgba(45, 112, 217, 0.15)' : '',
          borderRadius: isExpanded ? '16px 16px 0 0' : '16px',
          '& .MuiAccordionSummary-content': {
            margin: '0',
          },
        }}
      >
        <Typography>
          {dictionary.prescriptionMedicationsAmount(medications.length)}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: '16px 16px 0 16px' }}>
        {medications.map((medication, index) => (
          <PrescriptionMedicationDialogMobile
            key={index}
            medication={medication}
            isSupplied={isSupplied}
          />
        ))}
      </AccordionDetails>
    </Accordion>
  );
};
